/**
 * Rehband – Size Finder (snippets/size-finder.liquid)
 *
 * Start screen with the measurement input and a prominent "How to measure" guide (measuring steps) -> recommended
 * size -> "Select size M" selects the size in the theme's variant picker (Impact <variant-picker>: one radio input per
 * option value, value = option value id).
 *
 * - Size table from the metaobject (cm): [{ size: "Medium", label: "M", min: 35, max: 37 }, …]. The measurement is
 *   compared in the unit it was entered in, against the values the table shows (inch values rounded to one decimal),
 *   so a border in the table is a border for the finder, too:
 *     * on the border of two sizes (e.g. 35 cm / 13.8 in): "between two sizes", both offered equally + advice
 *     * within 0.5 cm (0.2 in) of a border: the size + a note that the neighbouring size could fit as well
 *     * below / above the table: contact link (+ "Did you mean 16 in?" when the other unit fits the table)
 * - The size option and its values are found by their names – option values may be translated per language
 *   ("Medium" -> "M"), so both the long name and the short label of the table are accepted.
 * - "Select size M" clicks the visible button of that size – the theme's or the one of a swatch app (Variant Image
 *   Wizard draws its own picker and hides Impact's inputs) – and falls back to the theme's radio input.
 * - Impact re-renders "liquid" blocks (this finder included) on every variant change. The trigger has a stable id
 *   (Impact restores focus by id), announcements use a live region outside the block and the result is kept in
 *   sessionStorage, so a new instance shows the same result.
 * - The last measurement is remembered per size table (like the knee sleeve finder): 30 days in localStorage when the
 *   visitor allows preference cookies (Shopify Customer Privacy API), otherwise for the current tab only.
 * - Events: DOM "size-finder:result" / "size-finder:select", GTM dataLayer (if present), Shopify custom pixel events
 *   (size_finder_open, size_finder_guide, size_finder_video, size_finder_result, size_finder_select).
 * - <size-finder-video>: click-to-load YouTube player (drawer and size chart page), stopped when its screen is left.
 */

(() => {
  if (window.customElements.get('size-finder')) return;

  const MIN_CM = 15;
  const MAX_CM = 90;
  const CM_PER_INCH = 2.54;
  // Distance to a border (in tenths of the unit) that counts as "close to the next size": 0.5 cm ≈ 0.2 in
  const NEAR = { cm: 5, in: 2 };
  const STORAGE_KEY = 'rehband:size-finder';
  const STORAGE_TTL = 30 * 24 * 60 * 60 * 1000;
  const UNIT_WORDS = {
    cm: ['cm', 'centimeter', 'centimeters', 'zentimeter', 'centimetri', 'centímetros', 'centimètres', 'centymetrów'],
    in: ['in', 'inch', 'inches', '"', '″', 'zoll', 'tum', 'duim', 'cali', 'pulgada', 'pulgadas', 'pouce', 'pouces', 'pollice', 'pollici'],
  };

  const fill = (template = '', values = {}) =>
    template.replace(/%(\w+)%/g, (placeholder, key) => (key in values ? values[key] : placeholder));

  /** Tenths of a number, as an integer (avoids floating point noise in comparisons). */
  const tenths = (value) => Math.round(value * 10);

  const normalize = (value) => {
    const text = String(value || '')
      .toLowerCase()
      .replace(/[\s_-]+/g, '');
    const long = { xxsmall: 'xxs', xsmall: 'xs', small: 's', medium: 'm', large: 'l', xlarge: 'xl', xxlarge: 'xxl', xxxlarge: 'xxxl' };
    return long[text] || text;
  };

  function publish(eventName, detail) {
    try {
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: eventName, size_finder: detail });
    } catch (error) {
      /* analytics must never break the finder */
    }
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(eventName, detail);
      }
    } catch (error) {
      /* analytics must never break the finder */
    }
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Storage (same rules as the knee sleeve finder)
     ------------------------------------------------------------------------------------------------------------- */

  function getStorage(type) {
    try {
      const storage = window[type];
      const probe = '__sf__';
      storage.setItem(probe, probe);
      storage.removeItem(probe);
      return storage;
    } catch (error) {
      return null;
    }
  }

  function preferencesAllowed() {
    try {
      const privacy = window.Shopify && window.Shopify.customerPrivacy;
      return Boolean(privacy && typeof privacy.preferencesProcessingAllowed === 'function' && privacy.preferencesProcessingAllowed());
    } catch (error) {
      return false;
    }
  }

  /** Loads Shopify's Customer Privacy API once (it is not always on the page). */
  let privacyPromise = null;
  function loadCustomerPrivacy() {
    if (privacyPromise) return privacyPromise;
    privacyPromise = new Promise((resolve) => {
      const shopify = window.Shopify;
      if (shopify && shopify.customerPrivacy) return resolve(shopify.customerPrivacy);
      if (!shopify || typeof shopify.loadFeatures !== 'function') return resolve(null);
      const timeout = setTimeout(() => resolve(null), 3000);
      try {
        shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
          clearTimeout(timeout);
          resolve(error ? null : window.Shopify.customerPrivacy || null);
        });
      } catch (error) {
        clearTimeout(timeout);
        resolve(null);
      }
    });
    return privacyPromise;
  }

  function readAll(type) {
    try {
      const data = JSON.parse(getStorage(type)?.getItem(STORAGE_KEY) || 'null');
      return data && typeof data === 'object' ? data : {};
    } catch (error) {
      return {};
    }
  }

  /**
   * The remembered measurement for a key. The key is the size table's "Measurement ID" (the same measurement for several
   * products, e.g. all knee sleeves: calf circumference 10 cm below the knee) or its handle; legacyKey is the handle
   * under which older versions stored it – such an entry is moved to the key once.
   */
  function readMeasurement(key, legacyKey) {
    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    const sources = ['sessionStorage'];
    if (preferencesAllowed()) {
      sources.push('localStorage');
    } else if (privacy) {
      // Consent was withdrawn: forget what was remembered before
      try {
        getStorage('localStorage')?.removeItem(STORAGE_KEY);
      } catch (error) {
        /* ignore */
      }
    }
    const valid = (entry) => entry && Number.isFinite(Number(entry.value)) && ['cm', 'in'].includes(entry.unit) && Date.now() - Number(entry.savedAt || 0) < STORAGE_TTL;
    for (const type of sources) {
      const entry = readAll(type)[key];
      if (valid(entry)) return { value: Number(entry.value), unit: entry.unit };
    }
    if (legacyKey && legacyKey !== key) {
      for (const type of sources) {
        const data = readAll(type);
        const entry = data[legacyKey];
        if (!valid(entry)) continue;
        try {
          data[key] = entry;
          delete data[legacyKey];
          getStorage(type)?.setItem(STORAGE_KEY, JSON.stringify(data));
        } catch (error) {
          /* storage full or blocked */
        }
        return { value: Number(entry.value), unit: entry.unit };
      }
    }
    return null;
  }

  function writeMeasurement(key, measurement) {
    if (!key || (window.Shopify && window.Shopify.designMode)) return;
    const types = preferencesAllowed() ? ['sessionStorage', 'localStorage'] : ['sessionStorage'];
    types.forEach((type) => {
      try {
        const data = readAll(type);
        data[key] = { value: measurement.value, unit: measurement.unit, savedAt: Date.now() };
        getStorage(type)?.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        /* storage full or blocked */
      }
    });
  }

  /* ---------------------------------------------------------------------------------------------------------------
     Page-wide helpers
     ------------------------------------------------------------------------------------------------------------- */

  // Focus rings on headings that receive focus on every step only help keyboard users (see knee-sleeve-finder-v2.js)
  let lastInput = 'none';
  document.addEventListener('keydown', () => (lastInput = 'keyboard'), true);
  document.addEventListener('pointerdown', () => (lastInput = 'pointer'), true);

  // Live region outside the finder: Impact replaces the finder right after a size was selected
  let announcer = null;
  function announce(message) {
    if (!announcer) {
      announcer = document.createElement('div');
      announcer.className = 'sr-only';
      announcer.setAttribute('role', 'status');
      announcer.dataset.sfAnnouncer = '';
      document.body.append(announcer);
    }
    announcer.textContent = '';
    window.setTimeout(() => (announcer.textContent = message), 50);
  }

  class SizeFinder extends HTMLElement {
    connectedCallback() {
      if (this.config) return;
      const configElement = this.querySelector('[data-sf-config]');
      if (!configElement) return;

      try {
        this.config = JSON.parse(configElement.textContent);
      } catch (error) {
        console.warn('[size-finder] Invalid configuration', error);
        return;
      }

      this.tableSizes = (this.config.sizes || [])
        .map((entry) => ({ ...entry, min: Number(entry.min), max: Number(entry.max) }))
        .filter((entry) => Number.isFinite(entry.min) && Number.isFinite(entry.max))
        .sort((a, b) => a.min - b.min);
      if (!this.tableSizes.length) {
        this.hidden = true;
        return;
      }
      // Only sizes the product is sold in (a size chart can be shared by products with fewer sizes)
      const sold = this.tableSizes.filter((size) => this.optionValue(size));
      this.sizes = sold.length ? sold : this.tableSizes;
      this.strings = this.config.strings || {};
      this.totalSteps = Number(this.config.steps) || 0;

      this.dialog = this.querySelector('[data-sf-dialog]');
      this.form = this.querySelector('[data-sf-form]');
      this.valueInput = this.querySelector('[data-sf-value]');
      this.error = this.querySelector('[data-sf-error]');
      this.formUnitHint = this.querySelector('[data-sf-form-unit-hint]');
      this.backButton = this.querySelector('[data-sf-back]');
      this.nextButton = this.querySelector('[data-sf-next]');
      this.body = this.querySelector('[data-sf-body]');
      this.step = 'start';

      this.querySelector('[data-sf-open]')?.addEventListener('click', () => this.open());
      this.querySelector('[data-sf-close]')?.addEventListener('click', () => this.dialog.close());
      this.querySelector('[data-sf-guide]')?.addEventListener('click', () => {
        publish('size_finder_guide', { product: this.config.product });
        this.goTo(1);
      });
      this.backButton?.addEventListener('click', () => this.back());
      this.nextButton?.addEventListener('click', () => this.next());
      this.form?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.evaluate();
      });
      this.valueInput?.addEventListener('input', () => this.clearError());
      this.querySelectorAll('[data-sf-unit]').forEach((input) => input.addEventListener('change', () => this.onUnitChange()));
      this.addEventListener('click', (event) => this.onAction(event));

      if (this.dialog) {
        this.dialog.addEventListener('close', () => this.onClose());
        this.dialog.addEventListener('click', (event) => {
          if (event.target !== this.dialog) return;
          const box = this.dialog.getBoundingClientRect();
          const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
          if (!inside) this.dialog.close();
        });
      }

      this.onConsentChange = () => {
        if (this.measurement) writeMeasurement(this.config.key, this.measurement);
      };
      document.addEventListener('visitorConsentCollected', this.onConsentChange);

      this.renderTable([]);
      this.goTo('start', { focus: false });
      this.restore();
      // The Customer Privacy API may arrive later: a measurement remembered for 30 days can only be read with it
      if (!this.measurement) loadCustomerPrivacy().then(() => this.isConnected && !this.measurement && this.restore());
    }

    disconnectedCallback() {
      if (this.onConsentChange) document.removeEventListener('visitorConsentCollected', this.onConsentChange);
    }

    /* ---------------------------------------------------------------------------------------------------------------
       Drawer and steps
       ------------------------------------------------------------------------------------------------------------- */

    open() {
      if (!this.dialog || this.dialog.open) return;
      this.opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      this.dialog.showModal();
      document.documentElement.classList.add('sf-scroll-lock');
      publish('size_finder_open', { product: this.config.product, saved: Boolean(this.measurement) });
      this.goTo(this.step);
    }

    onClose() {
      document.documentElement.classList.remove('sf-scroll-lock');
      this.querySelector('size-finder-video')?.stop?.();
      if (this.opener && this.opener.isConnected) this.opener.focus({ preventScroll: true });
      this.opener = null;
    }

    back() {
      if (this.step === 'result' || this.step === 'video' || this.step === 1) this.goTo('start');
      else if (typeof this.step === 'number') this.goTo(this.step - 1);
    }

    next() {
      if (this.step === 'video') return this.goTo('start', { target: 'input' });
      if (typeof this.step !== 'number') return;
      if (this.step >= this.totalSteps) this.goTo('start', { target: 'input' });
      else this.goTo(this.step + 1);
    }

    goTo(step, { focus = true, target = 'heading' } = {}) {
      this.step = step;
      this.querySelectorAll('[data-sf-step]').forEach((element) => {
        element.hidden = element.dataset.sfStep !== String(step);
      });

      const isStart = step === 'start';
      const isResult = step === 'result';
      const isVideo = step === 'video';
      const isLastStep = isVideo || (typeof step === 'number' && step >= this.totalSteps);
      // A playing video stops as soon as its screen is left
      if (!isVideo) this.querySelector('size-finder-video')?.stop?.();

      const progress = this.querySelector('[data-sf-progress]');
      const progressText = this.querySelector('[data-sf-progress-text]');
      const progressBar = this.querySelector('[data-sf-progress-bar]');
      if (progress) progress.hidden = isStart || isVideo;
      if (progressText) progressText.textContent = isResult ? this.strings.resultLabel || '' : fill(this.strings.stepOf, { current: step, total: this.totalSteps });
      if (progressBar) progressBar.style.width = `${isResult ? 100 : (Number(step) / this.totalSteps) * 100}%`;

      // Start screen: no footer (the form has its own button). Steps: back + next ("Enter measurement" on the last).
      const nav = this.querySelector('[data-sf-nav]');
      if (nav) {
        nav.hidden = isStart;
        nav.classList.toggle('is-back-only', isResult);
      }
      if (this.nextButton) {
        this.nextButton.hidden = isStart || isResult;
        this.nextButton.textContent = isLastStep ? this.strings.toInput : this.strings.next;
      }

      if (this.body) this.body.scrollTop = 0;
      if (!focus || !this.dialog?.open) return;

      const focusTarget = target === 'input' && this.valueInput ? this.valueInput : this.querySelector(`[data-sf-step="${step}"] [data-sf-heading]`);
      if (!focusTarget) return;
      if (focusTarget.matches('[data-sf-heading]') && lastInput !== 'keyboard') {
        focusTarget.classList.add('is-quiet-focus');
        focusTarget.addEventListener('blur', () => focusTarget.classList.remove('is-quiet-focus'), { once: true });
      }
      focusTarget.focus({ preventScroll: true });
    }

    /* ---------------------------------------------------------------------------------------------------------------
       Measurement
       ------------------------------------------------------------------------------------------------------------- */

    get unit() {
      return this.querySelector('[data-sf-unit]:checked')?.value === 'in' ? 'in' : 'cm';
    }

    setUnit(unit) {
      const input = this.querySelector(`[data-sf-unit][value="${unit}"]`);
      if (input) input.checked = true;
      const suffix = this.querySelector('[data-sf-unit-suffix]');
      if (suffix) suffix.textContent = unit;
    }

    onUnitChange() {
      this.setUnit(this.unit);
      this.clearError();
    }

    showError(message, alternative = null) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
      this.valueInput?.setAttribute('aria-invalid', 'true');
      this.renderUnitHint(this.formUnitHint, alternative);
      this.valueInput?.focus();
    }

    clearError() {
      this.renderUnitHint(this.formUnitHint, null);
      if (!this.error || this.error.hidden) return;
      this.error.hidden = true;
      this.error.textContent = '';
      this.valueInput?.removeAttribute('aria-invalid');
    }

    /** "36,5", "36.5 cm", "14.5 in", '14.5"', "14,5 Zoll" -> { value, unit } (unit null when not typed). */
    parse(text) {
      const raw = String(text || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');
      const match = raw.match(/^(\d+(?:[.,]\d+)?)\s*([^\d\s.,]*)\.?$/);
      if (!match) return null;
      const value = Number(match[1].replace(',', '.'));
      const word = match[2];
      if (!word) return { value, unit: null };
      const unit = Object.keys(UNIT_WORDS).find((key) => UNIT_WORDS[key].includes(word));
      return unit ? { value, unit } : null;
    }

    evaluate() {
      const text = (this.valueInput?.value || '').trim();
      if (!text) return this.showError(this.strings.errorEmpty);
      const parsed = this.parse(text);
      if (!parsed) return this.showError(this.strings.errorInvalid);
      if (parsed.unit && parsed.unit !== this.unit) this.setUnit(parsed.unit);

      const measurement = { value: parsed.value, unit: this.unit };
      const cm = this.toCm(measurement);
      if (cm < MIN_CM || cm > MAX_CM) return this.showError(this.strings.errorImplausible, this.alternative(measurement));

      this.clearError();
      this.measurement = measurement;
      writeMeasurement(this.config.key, measurement);
      this.showResult(measurement);
    }

    toCm({ value, unit }) {
      return unit === 'in' ? value * CM_PER_INCH : value;
    }

    /** The same number in the other unit, when that one fits the size table and the entered one does not. */
    alternative(measurement) {
      const other = { value: measurement.value, unit: measurement.unit === 'cm' ? 'in' : 'cm' };
      const fits = (entry) => ['size', 'between'].includes(this.match(entry).type);
      return !fits(measurement) && fits(other) ? other : null;
    }

    /** Size table in a unit, in tenths – exactly the values the table shows (inch values rounded to one decimal). */
    tableIn(unit) {
      return this.sizes.map((size) => ({
        size,
        min: unit === 'in' ? tenths(size.min / CM_PER_INCH) : tenths(size.min),
        max: unit === 'in' ? tenths(size.max / CM_PER_INCH) : tenths(size.max),
      }));
    }

    /** Size(s) for a measurement: one size (maybe close to the next), two sizes (border / gap) or outside the table. */
    match({ value, unit }) {
      const table = this.tableIn(unit);
      if (!table.length) return { type: 'none', sizes: [] };
      const v = tenths(value);
      const first = table[0];
      const last = table[table.length - 1];
      if (v < first.min) return { type: 'below', sizes: [first.size] };
      if (v > last.max) return { type: 'above', sizes: [last.size] };

      const matches = table.filter((row) => v >= row.min && v <= row.max);
      if (matches.length > 1) return { type: 'between', sizes: [matches[0].size, matches[1].size] };
      if (matches.length === 1) {
        const index = table.indexOf(matches[0]);
        const row = matches[0];
        const previous = table[index - 1];
        const following = table[index + 1];
        let near = null;
        if (previous && previous.max === row.min && v - row.min <= NEAR[unit]) near = previous.size;
        else if (following && following.min === row.max && row.max - v <= NEAR[unit]) near = following.size;
        return { type: 'size', sizes: [row.size], near };
      }

      // A gap in the table: offer the sizes on both sides
      const larger = table.findIndex((row) => row.min > v);
      return { type: 'between', sizes: [table[larger - 1].size, table[larger].size] };
    }

    format(value) {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, { maximumFractionDigits: 1 }).format(value);
    }

    formatValue({ value, unit }) {
      return `${this.format(Math.round(value * 10) / 10)} ${unit}`;
    }

    formatRange(size, unit) {
      const convert = (cm) => (unit === 'in' ? Math.round((cm / CM_PER_INCH) * 10) / 10 : cm);
      return `${this.format(convert(size.min))}–${this.format(convert(size.max))} ${unit}`;
    }

    /** Result texts, buttons and table for a measurement (also used silently for a remembered measurement). */
    renderResult(measurement) {
      const result = this.match(measurement);
      const { unit } = measurement;
      const heading = this.querySelector('[data-sf-result-heading]');
      const text = this.querySelector('[data-sf-result-text]');
      const near = this.querySelector('[data-sf-result-near]');
      const advice = this.querySelector('[data-sf-result-advice]');
      const notice = this.querySelector('[data-sf-result-notice]');
      const actions = this.querySelector('[data-sf-result-actions]');
      const contact = this.querySelector('[data-sf-contact]');
      const values = { measurement: this.config.measurement, value: this.formatValue(measurement) };
      const [first, second] = result.sizes;

      switch (result.type) {
        case 'size':
          heading.textContent = fill(this.strings.resultHeading, { size: this.label(first) });
          text.textContent = fill(this.strings.resultText, { ...values, size: this.label(first), range: this.formatRange(first, unit) });
          break;
        case 'between':
          heading.textContent = this.strings.betweenHeading;
          text.textContent = fill(this.strings.betweenText, { ...values, smaller: this.label(first), larger: this.label(second) });
          break;
        case 'below':
          heading.textContent = this.strings.belowHeading;
          text.textContent = fill(this.strings.belowText, { ...values, size: this.label(first), range: this.formatRange(first, unit) });
          break;
        default:
          heading.textContent = this.strings.aboveHeading;
          text.textContent = fill(this.strings.aboveText, { ...values, size: this.label(first), range: this.formatRange(first, unit) });
      }

      if (near) {
        near.textContent = result.near ? fill(this.strings.nearText, { size: this.label(result.near) }) : '';
        near.hidden = !result.near;
      }
      // "Select size M" buttons: two equal ones between two sizes, the recommended one filled when close to a border.
      // Two sizes always side by side, smaller first, each with its reason underneath (metaobject smaller_hint / larger_hint).
      let offer = [];
      if (result.type === 'between') offer = result.sizes.map((size) => ({ size, style: 'pair' }));
      else if (result.type === 'size') offer = [{ size: first, style: 'primary' }, ...(result.near ? [{ size: result.near, style: 'secondary' }] : [])];
      offer.sort((a, b) => a.size.min - b.size.min);
      if (contact) contact.hidden = offer.length > 0;

      const hints = this.config.hints || {};
      const withHints = offer.length === 2 && Boolean(hints.smaller && hints.larger);
      const choose = this.querySelector('[data-sf-result-choose]');
      if (choose) choose.hidden = !(withHints && result.type === 'between');
      if (advice) advice.hidden = !(result.type === 'between' || result.near) || withHints || !advice.textContent.trim();

      const notices = [];
      const options = offer.map(({ size, style }, index) => {
        const option = document.createElement('div');
        option.className = 'sf-result__option';
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `button sf-result__select sf-result__select--${style}`;
        button.dataset.sfSelect = size.size;
        button.textContent = fill(this.strings.select, { size: this.label(size) });
        const availability = this.availability(size);
        if (availability.missing) button.disabled = true;
        if (availability.soldOut) notices.push(fill(this.strings.unavailable, { size: this.label(size), variant: availability.variantName }));
        option.append(button);
        if (withHints) {
          const [title, ...rest] = String(index === 0 ? hints.smaller : hints.larger).split('\n');
          const hint = document.createElement('p');
          hint.className = 'sf-result__hint';
          hint.id = `${this.id}-hint-${index}`;
          const strong = document.createElement('strong');
          strong.textContent = title;
          hint.append(strong);
          if (rest.length) hint.append(' ', Object.assign(document.createElement('span'), { textContent: rest.join(' ') }));
          button.setAttribute('aria-describedby', hint.id);
          option.append(hint);
        }
        return option;
      });
      actions.replaceChildren(...options);
      actions.classList.toggle('is-pair', offer.length === 2);
      if (notice) {
        notice.textContent = notices.join(' ');
        notice.hidden = notices.length === 0;
      }

      this.renderUnitHint(this.querySelector('[data-sf-result-unit-hint]'), result.type === 'below' || result.type === 'above' ? this.alternative(measurement) : null);
      this.renderTable(result.sizes, result.type);
      this.updateTrigger(result, measurement);
      if (this.valueInput) this.valueInput.value = this.format(measurement.value);
      return result;
    }

    showResult(measurement) {
      const result = this.renderResult(measurement);
      this.goTo('result');

      const detail = {
        product: this.config.product,
        value: Math.round(measurement.value * 10) / 10,
        unit: measurement.unit,
        value_cm: Math.round(this.toCm(measurement) * 10) / 10,
        result: result.type,
        sizes: result.sizes.map((size) => size.size),
        near: result.near ? result.near.size : null,
      };
      this.dispatchEvent(new CustomEvent('size-finder:result', { bubbles: true, detail }));
      publish('size_finder_result', detail);
    }

    /** A remembered measurement: result ready, trigger shows the size. */
    restore() {
      const measurement = readMeasurement(this.config.key, this.config.legacyKey);
      if (!measurement) return;
      const cm = this.toCm(measurement);
      if (cm < MIN_CM || cm > MAX_CM) return;
      this.measurement = measurement;
      this.setUnit(measurement.unit);
      this.renderResult(measurement);
      this.goTo('result', { focus: false });
    }

    updateTrigger(result, measurement) {
      const title = this.querySelector('[data-sf-trigger-title]');
      const hint = this.querySelector('[data-sf-trigger-hint]');
      if (!title || !hint) return;
      let text = '';
      if (result.type === 'size') text = fill(this.strings.resultHeading, { size: this.label(result.sizes[0]) });
      if (result.type === 'between') text = fill(this.strings.savedBetween, { smaller: this.label(result.sizes[0]), larger: this.label(result.sizes[1]) });
      if (!text) {
        title.textContent = this.strings.open;
        hint.textContent = this.strings.openHint;
        this.querySelector('[data-sf-open]')?.classList.remove('is-saved');
        return;
      }
      title.textContent = text;
      hint.textContent = fill(this.strings.savedHint, { value: this.formatValue(measurement) });
      this.querySelector('[data-sf-open]')?.classList.add('is-saved');
    }

    renderUnitHint(container, alternative) {
      if (!container) return;
      container.hidden = !alternative;
      if (!alternative) return;
      const value = this.formatValue(alternative);
      container.querySelector('[data-sf-unit-hint-text]').textContent = fill(this.strings.unitHint, { value });
      const button = container.querySelector('[data-sf-action="unit"]');
      button.textContent = fill(this.strings.unitUse, { value });
      button.dataset.sfUnit = alternative.unit;
    }

    renderTable(highlighted = [], type = '') {
      const body = this.querySelector('[data-sf-table]');
      if (!body) return;
      const marked = new Set(highlighted.filter(Boolean).map((size) => size.size));
      body.replaceChildren(
        ...this.sizes.map((size) => {
          const row = document.createElement('tr');
          if (marked.has(size.size) && (type === 'size' || type === 'between')) row.className = 'is-match';
          const cells = [this.label(size), this.formatRange(size, 'cm').replace(' cm', ''), this.formatRange(size, 'in').replace(' in', '')];
          cells.forEach((value, index) => {
            const cell = document.createElement(index === 0 ? 'th' : 'td');
            if (index === 0) cell.scope = 'row';
            cell.textContent = value;
            row.append(cell);
          });
          return row;
        })
      );
    }

    /* ---------------------------------------------------------------------------------------------------------------
       Size option of the product
       ------------------------------------------------------------------------------------------------------------- */

    label(size) {
      const value = this.optionValue(size);
      return (value && value.name) || size.label || size.size;
    }

    /** The product option whose values match the size table best (usually "Size" / "Größe"). */
    get sizeOption() {
      if (this._sizeOption !== undefined) return this._sizeOption;
      const keys = new Set(this.tableSizes.flatMap((size) => [normalize(size.size), normalize(size.label)]));
      let best = null;
      let bestScore = 0;
      (this.config.options || []).forEach((option) => {
        const score = option.values.filter((value) => keys.has(normalize(value.name))).length;
        if (score > bestScore) {
          best = option;
          bestScore = score;
        }
      });
      this._sizeOption = best;
      return best;
    }

    optionValue(size) {
      const option = this.sizeOption;
      if (!option) return null;
      const keys = [normalize(size.size), normalize(size.label)];
      return option.values.find((value) => keys.includes(normalize(value.name))) || null;
    }

    /** Selected option value names from the variant picker (falls back to the first value of every option). */
    selectedOptions() {
      const picker = this.variantPicker();
      return (this.config.options || []).map((option) => {
        const checked = picker?.querySelector(`input[data-option-position="${option.position}"]:checked`);
        const value = checked ? option.values.find((entry) => String(entry.id) === checked.value) : null;
        return (value || option.values[0] || {}).name;
      });
    }

    availability(size) {
      const option = this.sizeOption;
      const value = this.optionValue(size);
      if (!option || !value) return { missing: true };
      const selected = this.selectedOptions();
      selected[option.position - 1] = value.name;
      const variant = (this.config.variants || []).find((entry) => entry.options.every((name, index) => name === selected[index]));
      if (!variant) return { missing: false, soldOut: false };
      const others = selected.filter((name, index) => index !== option.position - 1);
      return { missing: false, soldOut: !variant.available, variantName: others.join(' · ') };
    }

    variantPicker() {
      const handle = this.config.product;
      const scope = this.closest('.product-info, product-info, .product') || document;
      return (
        scope.querySelector(`variant-picker[handle="${CSS.escape(handle)}"]`) ||
        document.querySelector(`variant-picker[handle="${CSS.escape(handle)}"]`) ||
        scope.querySelector('variant-picker')
      );
    }

    onAction(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.dataset.sfSelect) {
        this.select(button.dataset.sfSelect);
        return;
      }
      switch (button.dataset.sfAction) {
        case 'again':
          this.goTo('start', { target: 'input' });
          break;
        case 'video':
          this.goTo('video');
          break;
        case 'unit':
          this.setUnit(button.dataset.sfUnit);
          this.evaluate();
          break;
        default:
          break;
      }
    }

    /** The theme's radio input for a size (queried fresh – Impact replaces the picker on every variant change). */
    sizeInput(value) {
      const option = this.sizeOption;
      const picker = this.variantPicker();
      if (!option || !value || !picker) return null;
      return picker.querySelector(`input[data-option-position="${option.position}"][value="${CSS.escape(String(value.id))}"]`);
    }

    /**
     * What a visitor would click for a size value – the theme's own swatch, or the one of an app that draws its own
     * picker over the theme's (Variant Image Wizard hides Impact's inputs and only updates its buttons when they are
     * used). The visible element has to show the value name and sit in a group with other values of the size option;
     * inside it, the real control is used: a label with its input, the app's own radio input, a button – or the element.
     * Apps win over the theme's picker when both are visible. Open shadow roots are searched as well.
     */
    visibleControl(value) {
      const option = this.sizeOption;
      if (!option || !value) return null;
      const scope = this.closest('.product-info, product-info, .product') || document;
      const target = normalize(value.name);
      const others = option.values.filter((entry) => entry !== value).map((entry) => normalize(entry.name));
      const interactive = 'label, button, a, input, [role="radio"], [role="option"], [role="button"], [data-value], [data-option-value]';
      const names = (element) =>
        [element.textContent, element.getAttribute('data-value'), element.getAttribute('data-option-value'), element.getAttribute('aria-label'), element.getAttribute('title'), element.matches('input') ? element.value : '']
          .filter(Boolean)
          .map((text) => normalize(text.trim()));
      const visible = (element) => {
        const box = element.getBoundingClientRect();
        return box.width >= 4 && box.height >= 4 && getComputedStyle(element).visibility !== 'hidden';
      };
      const allElements = (root) => {
        const list = [];
        root.querySelectorAll('*').forEach((element) => {
          list.push(element);
          if (element.shadowRoot) list.push(...allElements(element.shadowRoot));
        });
        return list;
      };
      const unlinkedLabel = (element) => {
        const label = element.closest('label[for]');
        return Boolean(label && !label.control);
      };

      const elements = allElements(scope);
      const matches = elements.filter(
        (element) =>
          !this.contains(element) &&
          !element.closest('dialog, script, template') &&
          names(element).includes(target) &&
          !unlinkedLabel(element) &&
          (element.matches(interactive) || getComputedStyle(element).cursor === 'pointer') &&
          visible(element)
      );
      const inGroup = (element) => {
        let group = element.parentElement || element.getRootNode().host;
        for (let depth = 0; group && depth < 4; depth += 1, group = group.parentElement) {
          const texts = [...group.querySelectorAll('*')].map((child) => normalize(child.textContent.trim()));
          if (others.filter((name) => texts.includes(name)).length >= Math.min(2, others.length)) return true;
        }
        return false;
      };
      // Outermost match (e.g. a swatch around a label around a span with the same text) inside a size group
      const swatches = matches.filter((element) => !matches.some((other) => other !== element && other.contains(element)) && inGroup(element));
      if (!swatches.length) return null;
      const swatch = swatches.find((element) => !element.closest('variant-picker')) || swatches[0];

      // The real control inside the swatch
      if (swatch.matches('label') && swatch.control) return swatch;
      const label = [...swatch.querySelectorAll('label[for]')].find((element) => element.control);
      if (label) return label;
      const input = swatch.matches('input[type="radio"], input[type="checkbox"]') ? swatch : swatch.querySelector('input[type="radio"], input[type="checkbox"]');
      if (input && !input.disabled) return input;
      return swatch.querySelector('button, [role="radio"], [role="option"], a[href]') || swatch;
    }

    /**
     * Variant Image Wizard (StarApps, "Rio" options) draws its own option buttons and hides Impact's variant picker.
     * Its integration exposes one callback per option name (window.__productWizRioOptions.cbs) – the same one its own
     * buttons use: it updates the app's buttons and forwards the value to Impact's input. Returns false if not there.
     */
    selectInSwatchApp(value) {
      const rio = window.__productWizRioOptions;
      const option = this.sizeOption;
      if (!rio || !rio.enabled || rio.phantom || !rio.cbs || !option) return false;
      const key = Object.keys(rio.cbs).find((name) => name === option.name) || Object.keys(rio.cbs).find((name) => normalize(name) === normalize(option.name));
      if (!key || typeof rio.cbs[key] !== 'function') return false;
      try {
        rio.cbs[key](value.name);
        return true;
      } catch (error) {
        console.warn('[size-finder] swatch app selection failed', error);
        return false;
      }
    }

    async select(sizeName) {
      const size = this.sizes.find((entry) => entry.size === sizeName);
      if (!size) return;
      const value = this.optionValue(size);
      const isSelected = () => Boolean(this.sizeInput(value)?.checked);
      const debug = /[?&]sf_debug=1/.test(window.location.search);

      // 1. A swatch app's own API, 2. what a visitor would click, 3. the theme's input as a fallback
      let via = 'input';
      let control = null;
      if (value && this.selectInSwatchApp(value)) {
        via = 'app';
        // The app forwards the value to the theme asynchronously
        for (let wait = 0; wait < 20 && !isSelected(); wait += 1) await new Promise((resolve) => setTimeout(resolve, 25));
      } else {
        control = this.visibleControl(value);
        if (control) {
          via = 'control';
          control.click();
        }
      }
      if (debug) console.info('[size-finder] select', sizeName, '→', via, control ? control.outerHTML.slice(0, 300) : '', window.__productWizRioOptions || 'no Variant Image Wizard options');
      const input = this.sizeInput(value);
      if (input && !input.checked && !input.disabled) input.click();
      const selected = isSelected() || (!input && via !== 'input');

      const detail = { product: this.config.product, size: sizeName, found: selected, via };
      // The theme may have re-rendered the finder in the meantime
      (this.isConnected ? this : document).dispatchEvent(new CustomEvent('size-finder:select', { bubbles: true, detail }));
      publish('size_finder_select', detail);

      if (!selected) {
        // The theme's size selection is not there (other selector style, value renamed …): say what to do instead
        const notice = this.querySelector('[data-sf-result-notice]');
        if (notice) {
          notice.textContent = fill(this.strings.selectFailed, { size: this.label(size) });
          notice.hidden = false;
          notice.focus?.({ preventScroll: false });
        }
        return;
      }

      announce(fill(this.strings.selected, { size: this.label(size) }));
      this.dialog?.close();
    }
  }

  /** Click-to-load YouTube player (snippets/size-finder-video.liquid): nothing is loaded before the visitor presses play. */
  class SizeFinderVideo extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-sf-video-play]');
      this.button?.addEventListener('click', () => this.play());
    }

    play() {
      if (this.querySelector('iframe') || !this.dataset.src) return;
      const iframe = document.createElement('iframe');
      iframe.src = this.dataset.src;
      iframe.title = this.dataset.title || '';
      iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
      iframe.allowFullscreen = true;
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      if (this.button) this.button.hidden = true;
      this.append(iframe);
      this.classList.add('is-playing');
      iframe.focus();
      const finder = this.closest('size-finder');
      publish('size_finder_video', { product: finder?.config?.product || null, page: finder ? 'product' : 'size_chart' });
    }

    stop() {
      const iframe = this.querySelector('iframe');
      if (!iframe) return;
      const hadFocus = iframe === document.activeElement;
      iframe.remove();
      this.classList.remove('is-playing');
      if (this.button) {
        this.button.hidden = false;
        if (hadFocus) this.button.focus({ preventScroll: true });
      }
    }
  }

  window.customElements.define('size-finder', SizeFinder);
  if (!window.customElements.get('size-finder-video')) window.customElements.define('size-finder-video', SizeFinderVideo);
})();
